import Vue from "vue";

import awsRestAPI from "@/services/api/awsRestAPI";
import evessioAPI from "@/services/api/evessioAPI";
import { evessioHttpService } from "@/services/api/digitalEventHTTPs";
import { GROUP_TYPES, USER_CONNECTIONS_STATUSES } from "@/helpers/constants";
import { getCsrfToken, getPage } from "../../../helpers/configHelpers";

export default {
  /**
   * Check user session
   * @return {Promise<null>}
   */
  async checkUserSession() {
    let response = await evessioHttpService.get("digital-event/check-user-session");

    return response.data;
  },

  /**
   * Get User info by email
   * @param commit
   * @param email {String}
   * @return {Promise<null>}
   */
  async getUserInfoByEmail({ commit }, { email }) {
    commit("setEmail", { email: email });
    let response = await evessioHttpService.post("digital-event/get-user-info-by-email", { email: email });
    commit("setUserInfo", response.data);

    return response.data;
  },

  /**
   * Get User object from current session
   * @param commit
   * @return {Promise<null>}
   */
  async getUser({ commit }) {
    let response = await evessioHttpService.get("digital-event/get-user");
    commit("setUser", response.data);
    return response.data;
  },

  /**
   * Login by email and password
   * @param state
   * @param commit
   * @param email {String}
   * @param password {String}
   * @return {Promise<void>}
   */
  async login({ commit }, { email, password }) {
    let response = await evessioHttpService.post("digital-event/login", {
      csrf_token: getCsrfToken(),
      admin_email: email,
      admin_password: password
    });
    commit("setUser", response.data);

    return response.data;
  },

  /**
   * Validate confirmation account code
   * @return {Promise<null>}
   */
  async validateAccountConfirmationCode({ commit }, { confirmationCode }) {
    let response = await evessioHttpService.post("digital-event/validate-account-confirmation-code", {
      code: confirmationCode
    });
    commit("setUser", response.data);

    return response.data;
  },

  /**
   * ReSend account confirmation via email
   * @return {Promise<null>}
   */
  async resendAccountConfirmation() {
    let response = await evessioHttpService.post("digital-event/resend-account-confirmation", {
      page_slug: getPage()
    });

    return response.data;
  },

  /**
   * Send Password Confirmation
   * @return {Promise<void>}
   */
  async sendPasswordConfirmation({ rootState }, { email }) {
    let response = await evessioHttpService.post("digital-event/send-password-confirmation", {
      csrf_token: getCsrfToken(),
      admin_email: email,
      page_slug: getPage(),
      digital_event_slug: rootState.event.context.SLUG
    });

    return response.data;
  },

  /**
   * Validate password confirmation code
   * @return {Promise<null>}
   */
  async validatePasswordConfirmationCode({ commit }, { email, confirmationCode }) {
    let response = await evessioHttpService.post("digital-event/validate-password-confirmation-code", {
      email: email,
      code: confirmationCode
    });
    commit("setUser", response.data);

    return response.data;
  },

  /**
   * Set new password
   * @return {Promise<null>}
   */
  async setNewPassword({ commit }, { password }) {
    let response = await evessioHttpService.post("digital-event/set-new-password", {
      csrf_token: getCsrfToken(),
      admin_password: password
    });
    commit("setUser", response.data);

    return response.data;
  },

  /**
   * Create account
   * @param state
   * @param commit
   * @param email {String}
   * @param firstName {String}
   * @param lastName {String}
   * @param password {String}
   * @return {Promise<null>}
   */
  async createAccount({ commit }, { email, firstName, lastName, password }) {
    let response = await evessioHttpService.post("digital-event/create-account", {
      csrf_token: getCsrfToken(),
      user_email: email,
      user_firstname: firstName,
      user_lastname: lastName,
      admin_password: password,
      confirm: true,
      page_slug: getPage()
    });
    commit("setUser", response.data);

    return response.data;
  },

  /**
   * Get User Roles
   * @param commit
   * @return {Promise<null>}
   */
  async getUserRoles({ commit }) {
    let response = await evessioHttpService.get("digital-event/user-roles");
    commit("setUserRoles", response.data);

    return response.data;
  },

  /**
   * Init user data - insert records with user`s info in DynamoDB
   * @return {Promise<void>}
   */
  async initUser({ commit, rootState, rootGetters }) {
    let response = await awsRestAPI.initUser({
      groups: [
        {
          type: GROUP_TYPES.EVENT,
          uuid: rootState.event.context.uuid
        },
        {
          type: GROUP_TYPES.ROOM,
          uuid: rootGetters["room/activeRoom"].uuid
        },
        {
          type: GROUP_TYPES.TABLE,
          uuid: rootGetters["tables/userTable"].uuid
        }
      ],
      profile: rootGetters["users/currentUserProfile"]
    });

    commit("calls/connectivity/setTestResultData", response.data.user.connectivity, { root: true });

    let connectivityResponse = await awsRestAPI.getUserConnectivity();
    if (connectivityResponse.data.connectivity) {
      commit("calls/connectivity/setTestResultData", connectivityResponse.data.connectivity, { root: true });
    }

    return response.data;
  },

  /**
   * Get user connections
   * @return {Promise<*>}
   */
  async getUserConnections({ commit }) {
    let response = await awsRestAPI.getUserConnections();
    commit("setUserConnections", response.data.connections);
    return response.data;
  },

  async createUserConnection({ commit, state, dispatch }, { targetUserUUID, isOnline = false }) {
    let isConnectionExists = state.userConnections.find(u => u.targetUserUUID === targetUserUUID);

    if (
      !isConnectionExists ||
      (isConnectionExists && isConnectionExists.connectionStatus === USER_CONNECTIONS_STATUSES.DELETED)
    ) {
      let response = await awsRestAPI.createUserConnection(targetUserUUID);
      commit("addUserConnection", Object.assign({}, response.data.result, { isOnline }));
      return response.data;
    } else if (isConnectionExists && isConnectionExists.connectionStatus === USER_CONNECTIONS_STATUSES.INACTIVE) {
      await dispatch("updateUserConnection", {
        targetUserUUID: targetUserUUID,
        status: parseInt(USER_CONNECTIONS_STATUSES.ACTIVE)
      });
    }
  },

  async updateUserConnection({ state }, { targetUserUUID, status }) {
    const connectionIndex = state.userConnections.findIndex(user => user.targetUserUUID === targetUserUUID);
    let connection = state.userConnections[connectionIndex];
    Vue.set(
      state.userConnections,
      connectionIndex,
      Object.assign(connection, { sk: `${status}${connection.sk.slice(1)}`, connectionStatus: "" + status })
    );
    await awsRestAPI.updateUserConnection(targetUserUUID, { status });
  },

  /**
   * Update User public profile
   * @param commit
   * @param profileData {Object}
   * @param imageData  {Object}
   * @return {Promise<void>}
   */
  async updateUserProfile({ state, commit }, { profileData, imageData }) {
    if (imageData.data) {
      let response = await evessioHttpService.post("forms/file_response", imageData.data, imageData.config);
      if (response.data.success === "true") {
        profileData.photo = response.data.images[0].file_value;
      }
    }
    await evessioAPI.updateUserProfile({
      userUUID: state.user.user_uuid,
      data: profileData
    });

    commit("updateUserProfile", profileData);
    commit(
      "room/updateStateUserInfo",
      {
        userUUID: state.user.user_uuid,
        data: { profile: profileData }
      },
      { root: true }
    );

    await awsRestAPI.updateUserInfo({ profile: profileData });
  },
  /**
   * Ban user
   * @param state
   * @param isBanned {Boolean}
   * @param userUUID  {String}
   */
  async banUser({ rootState }, { isBanned, userUUID }) {
    if (isBanned) {
      return await evessioAPI.banUser({ userUUID: userUUID, eventUUID: rootState.event.config.uuid });
    } else {
      return await evessioAPI.restoreUser({ userUUID: userUUID, eventUUID: rootState.event.config.uuid });
    }
  }
};
